import Loading from '../loading/Loading.jsx';
import MergedTableHeader from './MergedTableHeader.jsx';
import Pagination from './Pagination.jsx';

import '../../../css/data-grid.css';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle.js';
import { useSubIcon } from '../../../hooks/useSubIcon.js';
import { useTableArrowNav } from '../../../hooks/useTableArrowNav.js';
import { decodeHtmlEntities } from '../../../utils/htmlEntity.js';

/**
 * @gird높이설정 해당 컴포넌트의 부모 요소에 dispaly: flex; 가 꼭 있어야 함.(*flex-grow 적용 default)
                !! 자동 높이 조절이 안될 경우 높이가 부모 요소에 min-height: 0; 이 적용되어 있는지 확인(부모태그 높이 설정 필수)
 * @공통NewDataGrid 해당 컴포넌트로 넘기는 props
   <NewDataGrid
        propsInfo={propsInfo}
        state={state}
        pageInfo={pageInfo}
        setPageInfo={setPageInfo}
        rowspan={2}
        selectCellParams={selectCellParams} // 옵션 값(그리드 onClick을 사용 시에만 props 넘기기)
        handleOnClick={handleOnClick} // 옵션 값(그리드 onClick을 사용 시에만 props 넘기기)
        selectRow={selectRow} // 옵션 값(그리드 onClick을 사용 시에만 props 넘기기)
    />
 * @propsInfo props로 넘길 정보 묶음 - data grid
  -- 괄호 안의 변수는 해당 컴포넌트에 직접적인 영향은 없으나 검색 버튼에 함께 넘기는 값으로 비어있으면 안됨
    dataHead: 그리드에 보여질 헤더 이름
    dataColumn: 헤더 이름과 매칭되는 데이터 컬럼명
    (apiCall: 검색 URL)
    (apiCntCall: 데이터 카운터 URL (사용하지 않으면 빈 문자열))
    (excelFileName: 엑셀 파일 이름 (엑셀 다운로드가 필요없는 화면은 빈 문자열))
    excelHead: 엑셀 다운로드 시 그리드 헤더가 보여지는 화면과 다를 때 추가 작성 (dataHead와 같으면 빈 배열)
    (colWidth: 필요한 컬럼의 너비를 지정할 때 사용(사용하지 않으면 삭제))
    (wrapTF: 컬럼의 줄바꿈이 필요할 때 사용(사용하지 않으면 삭제))
  <sample>
   -- 필요한 정보를 각각 변수를 선언해서 담아준다
    const dataHead = ['번호', '내원일자', '시간', '등록번호', '이름', '나이/성별', '상태', '전송일자'];
    const dataColumn = ['SEQ', 'PTMIINDT', 'PTMIINTM', 'PTMIIDNO', 'PTMINAME', 'PTMISEXX', 'PTMISTAT', 'PTMIWKDT'];
    const apiCall = '/jinryo/selectEMIHPTMIList.do';
    const apiCntCall = '/jinryo/selectEMIHPTMIListCnt.do';
    const excelFileName = '대상환자조회';
    const excelHead = [];
   -- 변수를 propsInfo에 담아 props로 넘겨준다
    const propsInfo = {
        dataHead: dataHead, 
        dataColumn: dataColumn, 
        apiCall: apiCall, 
        apiCntCall: apiCntCall,
        excelFileName: excelFileName,
        excelHead: excelHead,
        colWidth: ['3%', '4%', '8%', '15%', '6%', '4%', '8%', '30%', '5%'],
        wrapTF: true
    }
 * @state @setState 기본적으로 필요한 useState 기본 세팅 값(dataList, isSearched, isLoading)
  <sample>
   -- 세팅값은 기본이니 그대로 작성하여 props로 state, setState를 넘겨준다
    const [state, setState] = useState({
        dataList: [],
        isSearched: false,
        isLoading: false,
    })
 * @pageInfo @setPageInfo 페이지네이션 사용 여부, 전체 데이터 수
  <sample>
   -- pagination 사용 여부에 따라  paginationTF를 boolean 값으로 적기
   -- pagination을 사용하지 않아도 currentPage, itemsPerPage는 그대로 넘겨주기
    const [pageInfo, setPageInfo] = useState({
        paginationTF: false,
        currentPage: 1,
        itemsPerPage: 50, // 한 페이지당 보여줄 데이터 수
        totalItems: 0, // 전체 데이터 수
    });
 * @mergeConfig 셀 병합이 필요한 컬럼의 조건을 설정
    {key: value , row: value , nullable:'boolean'}의 형태로, key는 병합하고자 하는 컬럼이름을 적고 row는 최대 몇개를 병합할 지 적어준다
 * @selectCellParams 그리드 onClick시 가져올 컬럼명을 배열 형태로 넘겨주기
    const selectCellParams = ['USERIDNO', 'USERNAME'];
 * @selectRow 선택된 cell의 key값을 담아줄 state 선언
    const [selectRow, setSelectRow] = useState();
 * @handleOnClick 그리드 onClick시 수행할 함수(자세한 샘플은 UserMng.jsx 참고)
   -- onClick시 가져온 컬럼 data와 선택한 행의 key값을 담을 useState를 선언해주고,
    const [selectUser, setSelectUser] = useState({
        useridno: '',
        username: ''
    });
   -- selectCellParams에 맞게 api call params와 선택된 key값을 담아준다
    const handleOnClick = (dataList, index, selectCellParams, key) => {
        setSelectUser({
            useridno: dataList[index][selectCellParams[0]],
            username: dataList[index][selectCellParams[1]]
        })
        setSelectRow(key);
    };
 * @flag 외상 팝업에서 해당 그리드 사용시 디자인 색상 적용이 불가하여 추가 (DutySearch_Popup.jsx)
 */

const NewDataGrid = ({ propsInfo, state, pageInfo, setPageInfo, mergeConfig, selectCellParams, handleOnClick, selectRow, flag }) => {

    const dataList = state.dataList;
    const isSearched = state.isSearched;
    const isLoading = state.isLoading;

    const dataHead = propsInfo.dataHead;
    const dataColumn = propsInfo.dataColumn;
    const excelHead = propsInfo.excelHead;
    const colWidth = propsInfo.colWidth;
    const tdWrap = propsInfo.wrapTF;

    const paginationTF = pageInfo.paginationTF;
    
    const notOnClick = selectCellParams === undefined;

    const onHandlePage = (page) => {
        if (page !== pageInfo.currentPage) {
            setPageInfo(prev => ({
                ...prev,
                currentPage: page
            }));
        }
    };

    const paginationInfo = {
        ...pageInfo,
        onPageChange: onHandlePage,
    }

    const computeRowSpans = (dataList, mergeConfig = [], allColumns = []) => {
        const rowSpans = dataList.map(() => ({}));
        // 병합 기준이 되는 마스터 컬럼
        const masterMerge = mergeConfig.find(m => m.row === null);
        const masterKey = masterMerge?.key;
        // 1. 기준 컬럼 기준으로 병합 구간 나누기
        const mergeBlocks = [];
        let blockStart = 0;
        for (let i = 1; i <= dataList.length; i++) {
            const curr = dataList[i];
            const prev = dataList[i - 1];
            const isSameGroup =
                i < dataList.length &&
                dataList[i][masterKey] === dataList[i - 1][masterKey] &&
                dataList[i][masterKey] !== null &&
                dataList[i][masterKey] !== '';
            if (!isSameGroup) {
                mergeBlocks.push({ start: blockStart, end: i });
                blockStart = i;
            }
        }
        // 2. 기준 컬럼 병합 수행
        mergeBlocks.forEach(({ start, end }) => {
            const masterValue = dataList[start][masterKey];
            rowSpans[start][masterKey] = end - start;
            for (let i = start + 1; i < end; i++) {
                rowSpans[i][masterKey] = 0;
            }
            // 3. 그 외 병합 대상 칼럼 처리
            mergeConfig.forEach(({ key: colKey, row, nullable }) => {
                if (colKey === masterKey) return;
                let prevValue = null;
                let localStart = start;
                let mergeCount = 1;
                for (let i = start; i < end; i++) {
                    const currVal = dataList[i][colKey];
                    const isMergeable = nullable
                        ? currVal === prevValue
                        : currVal === prevValue && currVal !== '' && currVal !== null;
                    if (i === start) {
                        rowSpans[i][colKey] = 1;
                        prevValue = currVal;
                        continue;
                    }
                    if (isMergeable) {
                        rowSpans[localStart][colKey] += 1;
                        rowSpans[i][colKey] = 0;
                        mergeCount += 1;
                    } else {
                        rowSpans[i][colKey] = 1;
                        localStart = i;
                        mergeCount = 1;
                        prevValue = currVal;
                    }
                }
            });
        });
        // 4. 병합 대상이 아닌 컬럼 처리
        dataList.forEach((_, i) => {
            allColumns.forEach(colKey => {
                if (!(colKey in rowSpans[i])) {
                    rowSpans[i][colKey] = 1;
                }
            });
        });
        return rowSpans;
    };

    const rowSpans = computeRowSpans(dataList, mergeConfig ?? [], dataColumn);
    const colSpan = excelHead.length < 1 ? dataHead.length : excelHead.length;

    // Dynamic Style Hook
    const { lightBackColor, borderColorGray, hoverFontColor, selectGridCell } = useDynamicStyle();

    // icon list
    const { EmptyIcon, NullIcon } = useSubIcon();

    const tableRef = useTableArrowNav(dataList);

    return (
        <>
            <div className='data_grid_container'>
                {isLoading ? <Loading /> :
                    <div className='flex flex-col w-full h-full overflow-hidden'>
                        <div className='data_responsive'>
                            <hr className={`border-t-2 mr-1-1 ${flag ? 'tr_border_soft' : borderColorGray}`} />
                            <div className={`data_list_table ${tdWrap && 'td_wrap'} ${isSearched ? 'grid-animation' : ''}`}>
                                <table>
                                    {colWidth !== undefined &&
                                        <colgroup>
                                            {colWidth.length > 0 && colWidth.map((per, idx) => <col key={idx} width={per} /> )}
                                        </colgroup>
                                    }
                                    <MergedTableHeader
                                        headers={dataHead}
                                    />
                                    <tbody ref={tableRef}>
                                        {dataList.length === 0 ? (
                                            <tr>
                                                <td colSpan={1000} className='empty_box'>
                                                    <span className={`data_empty ${flag ? 'bg_tr_odd' : lightBackColor}`}>
                                                        {/* <div className='flex flex-col items-center gap-5 co_gray bold w-half'> */}
                                                            {isSearched ? EmptyIcon : NullIcon}
                                                            {isSearched ? '해당하는 데이터가 없습니다.' : '검색 버튼을 눌러 데이터를 조회하세요.'}
                                                        {/* </div> */}
                                                    </span>
                                                </td>
                                            </tr>
                                        ) : (
                                            dataList.map((item, rowIndex) => {
                                                const key = notOnClick ? rowIndex : item[selectCellParams[0]] + rowIndex;
                                                const select = selectRow === key;
                                                const gridColor = rowIndex % 2 === 0 ? flag ? 'bg_tr_odd' : lightBackColor : '';
                                                // 선택된 데이터가 있다면 포커스 바로 이동
                                                const hasSelectedRow = dataList.some((row, rIdx) => selectRow === (notOnClick ? rIdx : row[selectCellParams[0]] + rIdx));
                                                let targetTabIndex = -1;
                                                if (hasSelectedRow) {
                                                    if (select) targetTabIndex = 0;
                                                } else {
                                                    if (rowIndex === 0) targetTabIndex = 0;
                                                }
                                                return (
                                                    <tr key={key}
                                                        className={`focus_tr_style ${gridColor} ${hoverFontColor} ${!notOnClick && 'cursor-pointer'} ${!notOnClick && select && selectGridCell}`}
                                                        onClick={() => notOnClick ? '' : handleOnClick(dataList, rowIndex, selectCellParams, key)}
                                                    >
                                                        {dataColumn.map((col, colIndex) => {
                                                            const span = rowSpans[rowIndex][col];
                                                            const style = dataList[rowIndex].ISS_DIV === '전체' || dataList[rowIndex].TRMSEMNM === '전체' ? { fontWeight: 'bold' } : undefined
                                                            if (span) {
                                                                const isFocusTargetCell = !notOnClick && colIndex === 0;
                                                                return (
                                                                    <td key={colIndex} rowSpan={span} style={style}
                                                                        className={isFocusTargetCell ? 'arrow_target' : ''}
                                                                        tabIndex={isFocusTargetCell ? targetTabIndex : undefined}
                                                                        role={isFocusTargetCell ? 'link' : undefined}
                                                                        onKeyDown={(e) => {
                                                                            if (isFocusTargetCell && (e.key === 'Enter' || e.key === ' ')) {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                handleOnClick(dataList, rowIndex, selectCellParams, key);
                                                                            }
                                                                        }}>
                                                                        {decodeHtmlEntities(item[col])}
                                                                    </td>
                                                                );
                                                            } else {
                                                                return null;
                                                            }
                                                        })}
                                                    </tr>
                                                )
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <hr className='mr-1-1 co_border_gray' />
                        </div>
                        {dataList.length === 0 || !paginationTF ? (
                            <br />
                        ) : (
                            <Pagination
                                paginationInfo={paginationInfo}
                            />
                        )}
                    </div>
                }
            </div >
        </>
    );
};

export default NewDataGrid;